import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTracker } from "@dpdgroupuk/react-event-tracker";

import RightDrawerModal from "../../RightDrawerModal";
import {
  DRIVER_REQUEST,
  TRANSFER,
  UNABLE_TRANSFER_TO_LOCKED_ROUTE,
  DISMISS,
  TRANSFER_HIGH_VALUE_PARCEL,
} from "../../../constants/strings";
import DriverButtons from "./components/DriverButtons";
import TransferButtons from "./components/TransferButtons";
import {
  onAcceptRequest,
  onDeferRequest,
  onRejectRequest,
  onConfirmTransferRequest,
  getAvailableRoutesTransfer,
} from "../../../store/actions/request";

import {
  getRequestType,
  getDriverRequestTableTitle,
  prepareRequestParcels,
  getTableColumnsConfig,
  getPickupTableTitleSubtitle,
  getPickupColumnsConfig,
} from "../../../features/request/model";
import SelectDriverRoute from "./components/SelectDriverRoute";
import { getCurrentUserId } from "../../../store/selectors/user";
import { getRoute } from "../../../store/selectors/route";
import useLocationQueryParams from "../../../hooks/useLocationQueryParams";

import {
  getPlannedRequestsForReject,
  isPlannedAddRequest,
} from "../../../models/request";
import {
  isRemoveCollectionRequest,
  isRemoveParcelRequest,
} from "../../../models/routeDetail";
import { DRIVER_REQUEST_TYPE } from "../../../enums/requestTypes";
import ModalTable from "../../common/Table/ModalTable";
import { isRouteLocked } from "../TransferTo/utils";
import { showNotification } from "../../../store/actions/notifications";
import { NotificationType } from "../../../features/Notifier";
import {
  DRIVER_REQUESTS_MODAL,
  TRANSFER_REQUESTS_MODAL,
} from "../../../constants/analytics";
import useLoader from "../../../hooks/useLoader";
import {
  ACCEPT_DRIVER_REQUEST_PROCESS,
  CONFIRM_DRIVER_REQUEST_PROCESS,
  REJECT_DRIVER_REQUEST_PROCESS,
} from "../../../enums/loaderProcess";
import { hasHighValueParcel } from "../../../models/parcel";
import StaticBanner from "../../StaticBanner";

const DriverRequests = ({ open, onClose, stop = {}, route = {} }) => {
  const { requests } = stop;
  const dispatch = useDispatch();
  const [screen, setActiveScreen] = useState(DRIVER_REQUEST);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routes, setRoutes] = useState([]);
  const isDriverScreen = screen === DRIVER_REQUEST;
  const query = useLocationQueryParams();
  const uid = useSelector(getCurrentUserId);
  const { isSubmitting } = useSelector(getRoute);
  const { depot, date } = query;
  const driverRequestType = getRequestType(stop);
  const getTitle = useCallback(
    () => (isDriverScreen ? DRIVER_REQUEST : TRANSFER).toUpperCase(),
    [isDriverScreen]
  );
  const tracker = useTracker();
  const [showLoader, hideLoader] = useLoader();

  const onDefer = useCallback(() => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_DEFER);
    dispatch(onDeferRequest(requests));
    onClose();
  }, [onClose, requests, dispatch, tracker]);

  const onReject = useCallback(async () => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_REJECT);
    showLoader(REJECT_DRIVER_REQUEST_PROCESS);

    const requestsForReject = getPlannedRequestsForReject(requests);
    let isSuccess = false;
    if (requestsForReject.length) {
      isSuccess = await dispatch(onRejectRequest(requestsForReject));
    }

    if (requestsForReject.length !== requests.length) {
      setActiveScreen(TRANSFER);
    } else {
      isSuccess && onClose();
    }

    hideLoader(REJECT_DRIVER_REQUEST_PROCESS);
  }, [onClose, requests, showLoader, hideLoader, dispatch, tracker]);

  const onAccept = useCallback(async () => {
    tracker.logEvent(DRIVER_REQUESTS_MODAL.CLICK_ACCEPT);
    try {
      showLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
      if (
        isRemoveParcelRequest(requests[0]) ||
        isRemoveCollectionRequest(requests[0])
      ) {
        setActiveScreen(TRANSFER);
      } else {
        const isSuccess = await dispatch(onAcceptRequest(stop));
        isSuccess && onClose();
      }
    } finally {
      hideLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
    }
  }, [onClose, stop, requests, showLoader, hideLoader, dispatch, tracker]);

  const onConfirm = useCallback(async () => {
    tracker.logEvent(TRANSFER_REQUESTS_MODAL.CLICK_CONFIRM);
    showLoader(CONFIRM_DRIVER_REQUEST_PROCESS);

    const generatedRequests = await dispatch(
      onConfirmTransferRequest(stop, selectedRoute)
    );
    generatedRequests && onClose();

    hideLoader(CONFIRM_DRIVER_REQUEST_PROCESS);
  }, [onClose, stop, selectedRoute, showLoader, hideLoader, dispatch, tracker]);

  const onClickClose = useCallback(() => {
    tracker.logEvent(
      isDriverScreen
        ? DRIVER_REQUESTS_MODAL.CLICK_CLOSE
        : TRANSFER_REQUESTS_MODAL.CLICK_CLOSE
    );
    onClose();
  }, [isDriverScreen, onClose, tracker]);

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(depot, date, uid)
      );

      setRoutes(availableRoutes);
    };
    if (!isDriverScreen) {
      fetchRoutes();
    }
  }, [date, depot, isDriverScreen, stop, uid, dispatch]);

  const getActions = useCallback(
    () =>
      isDriverScreen ? (
        <DriverButtons
          onDefer={onDefer}
          onReject={onReject}
          onAccept={onAccept}
          isDisabledAcceptButton={isSubmitting}
        />
      ) : (
        <TransferButtons
          onClickConfirm={onConfirm}
          disabled={!selectedRoute || isRouteLocked(routes, selectedRoute)}
        />
      ),
    [
      isDriverScreen,
      onDefer,
      onReject,
      onAccept,
      isSubmitting,
      onConfirm,
      selectedRoute,
      routes,
    ]
  );

  const getTableColumns = useCallback(() => getTableColumnsConfig(stop), [
    stop,
  ]);
  const getParcelData = useCallback(() => prepareRequestParcels(stop, route), [
    route,
    stop,
  ]);
  const getTableTitle = useCallback(() => getDriverRequestTableTitle(stop), [
    stop,
  ]);

  const getPickupColumns = useCallback(() => getPickupColumnsConfig(stop), [
    stop,
  ]);
  const getPickupTableTitle = useCallback(
    () => getPickupTableTitleSubtitle(stop).title,
    [stop]
  );
  const getPickupTableSubtitle = useCallback(
    () => getPickupTableTitleSubtitle(stop).subtitle,
    [stop]
  );

  const selectDriverRoute = (value) => {
    if (isRouteLocked(routes, value)) {
      dispatch(
        showNotification({
          key: "transfer-locked-route-error",
          type: NotificationType.ACTION,
          message: UNABLE_TRANSFER_TO_LOCKED_ROUTE,
          buttonText: DISMISS,
        })
      );
    }
    setSelectedRoute(value);
  };

  const showHighValueParcelNotification = useMemo(
    () =>
      isDriverScreen
        ? isPlannedAddRequest(requests[0]) && hasHighValueParcel(requests)
        : hasHighValueParcel(requests),
    [isDriverScreen, requests]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClickClose}
      title={getTitle()}
      loadId={
        isDriverScreen
          ? DRIVER_REQUESTS_MODAL.LOAD
          : TRANSFER_REQUESTS_MODAL.LOAD
      }
      actions={getActions()}
    >
      {showHighValueParcelNotification && (
        <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
      )}

      <ModalTable
        title={getTableTitle()}
        columns={getTableColumns()}
        data={getParcelData()}
      />
      {driverRequestType === DRIVER_REQUEST_TYPE.PICKUP && (
        <ModalTable
          title={getPickupTableTitle()}
          subTitle={getPickupTableSubtitle()}
          columns={getPickupColumns()}
          data={getParcelData()}
        />
      )}
      {!isDriverScreen && (
        <SelectDriverRoute
          options={routes}
          onChange={selectDriverRoute}
          value={selectedRoute}
        />
      )}
    </RightDrawerModal>
  );
};

export default DriverRequests;
